import { Head, Link, router, usePage } from "@inertiajs/react";
import {
  Calendar,
  CheckCircle2,
  ShoppingCart,
  Trash2,
  User,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import CommentsThread from "@/components/comments-thread";
import { CreateDirectPurchaseDialog } from "@/components/create-direct-purchase-dialog";
import {
  EditableDataTable,
  type EditableDataTableAction,
} from "@/components/editable-data-table";
import InlineEditableAsyncCombobox from "@/components/inline-editable-async-combobox";
import { MarkAsReviewedDialog } from "@/components/mark-as-reviewed-dialog";
import PurchaseRequestPickerWithPreview from "@/components/purchase-request-picker-with-preview";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Separator } from "@/components/ui/separator";
import { useAutoRefreshOnProcessing } from "@/hooks/use-auto-refresh-on-processing";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import AppLayout from "@/layouts/app-layout";
import {
  organizationInvoiceItemPath,
  organizationInvoicePath,
  organizationInvoicesPath,
} from "@/routes";
import type {
  BreadcrumbItem,
  Comment,
  Invoice,
  InvoiceItem,
  RequestItem,
  SharedData,
} from "@/types";
import { formatDateAR, parseNumericInput } from "@/utils/date";

import AttachmentsSection from "./components/attachments-section";
import FinancialSummary from "./components/financial-summary";
import InvoiceHeader from "./components/invoice-header";
import { createItemColumns } from "./item-columns";

interface InvoiceShowProps {
  invoice: Invoice & {
    invoice_files: { url: string; filename: string }[];
    processed_pdf: { url: string; filename: string } | null;
    can_update: boolean;
    can_delete: boolean;
    has_purchase_request: boolean;
    can_be_marked_as_reviewed: boolean;
    can_create_direct_purchase: boolean;
    created_by: { id: number; full_name: string; email: string } | null;
    reviewed_by: { id: number; full_name: string; email: string } | null;
    available_request_items: RequestItem[];
  };
  items: InvoiceItem[];
  // sectors removed: dialog uses async search instead of preloaded list
}

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "outline" | "default" | "secondary" | "destructive";
  }
> = {
  uploaded: { label: "Cargada", variant: "outline" },
  processing: { label: "Procesando", variant: "secondary" },
  pending_review: { label: "Pendiente de revisión", variant: "outline" },
  reviewed: { label: "Revisada", variant: "default" },
  error: { label: "Error", variant: "destructive" },
};

export default function InvoiceShow({ invoice, items }: InvoiceShowProps) {
  // Comments are provided in the top-level Inertia props by the controller
  const pageProps = usePage().props as Record<string, unknown>;
  const comments = (pageProps.comments as Comment[] | undefined) ?? [];
  const shared = usePage().props as Partial<SharedData>;
  const routes = useOrganizationRoutes();
  const invoicesHref = organizationInvoicesPath(routes.organizationSlug);
  const invoiceHref = organizationInvoicePath(
    routes.organizationSlug,
    invoice.id,
  );
  const invoiceItemsPath = (itemId: number) =>
    organizationInvoiceItemPath(routes.organizationSlug, itemId);
  const markReviewedHref = routes.invoices.markAsReviewed(invoice.id);
  const createDirectPurchaseHref = routes.invoices.createDirectPurchase(
    invoice.id,
  );
  const supplierSearchEndpoint = routes.suppliers.search();
  const commentsHref = routes.invoices.comments(invoice.id);
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Facturas",
      href: invoicesHref,
    },
    {
      title: invoice.invoice_number ?? `#${invoice.id}`,
      href: invoiceHref,
    },
  ];

  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null);
  const [showMarkReviewedDialog, setShowMarkReviewedDialog] = useState(false);
  const [showDirectPurchaseDialog, setShowDirectPurchaseDialog] =
    useState(false);
  const [isCreatingDirectPurchase, setIsCreatingDirectPurchase] =
    useState(false);

  const { isSupervisor } = useResourcePermissions(invoice);

  // Only supervisor can edit items
  const canEditItems =
    isSupervisor &&
    invoice.status !== "processing" &&
    invoice.status !== "reviewed";
  // Extract unit options from shared data
  const unitOptions = shared.enums?.units ?? [
    { label: "UN - Unidades", value: "UN" },
  ];

  // Memoize columns to avoid unnecessary recalculations
  const itemColumns = useMemo(
    () => createItemColumns(canEditItems, invoice.currency, unitOptions),
    [canEditItems, invoice.currency, unitOptions],
  );

  // Derived alerts and flags
  const showProcessingAlert = invoice.status === "processing";
  const hasAttachments =
    Boolean(invoice.processed_pdf) || invoice.invoice_files.length > 0;

  // Auto-refresh page while processing
  useAutoRefreshOnProcessing(invoice.status);

  // Build payload for editable invoice item fields
  const buildInvoiceItemPayload = useCallback(
    (
      columnId: string,
      value: unknown,
    ): Record<string, string | number> | null => {
      const payload: Record<string, string | number> = {};

      // String fields
      if (columnId === "description_extracted") {
        payload[columnId] = typeof value === "string" ? value : "";
      }
      // Unit field (comes as AsyncOption from combobox)
      else if (columnId === "unit") {
        const option = value as { value: string; label: string } | null;
        payload[columnId] = option ? option.value : "";
      }
      // Numeric fields
      else if (["quantity", "unit_price", "vat_rate"].includes(columnId)) {
        payload[columnId] = parseNumericInput(value as string | number);
      }
      // Non-editable/calculated fields
      else {
        return null;
      }

      return payload;
    },
    [],
  );

  // Commit cell changes: only editable fields, not calculated ones
  const handleCellCommit = useCallback(
    ({
      rowId,
      columnId,
      value,
    }: {
      rowId: string | number;
      columnId: string;
      value: unknown;
      row: InvoiceItem;
    }) => {
      if (!canEditItems) {
        return Promise.resolve();
      }

      const payload = buildInvoiceItemPayload(columnId, value);
      if (!payload) {
        // Non-editable field, skip
        return Promise.resolve();
      }

      const id = Number(rowId);

      return new Promise<void>((resolve, reject) => {
        router.patch(
          invoiceItemsPath(id),
          { invoice_item: payload },
          {
            preserveScroll: true,
            preserveState: true,
            only: ["items", "invoice"],
            onSuccess: () => {
              setCellErrors((prev) => {
                const next = { ...prev };
                delete next[`${rowId}:${columnId}`];
                return next;
              });
              resolve();
            },
            onError: (errors) => {
              const message =
                (errors[columnId] as string | undefined) ??
                (Object.values(errors)[0] as string | undefined) ??
                "No se pudo guardar el cambio";
              setCellErrors((prev) => ({
                ...prev,
                [`${rowId}:${columnId}`]: message,
              }));
              reject(new Error(message));
            },
          },
        );
      });
    },
    [canEditItems, buildInvoiceItemPayload, invoiceItemsPath],
  );

  // Add new item: only supervisor
  // Delete item: only supervisor
  const handleDeleteItem = useCallback(
    (item: InvoiceItem) => {
      if (!canEditItems) {
        return Promise.resolve();
      }

      const id = Number(item.id);
      setDeletingItemId(id);

      return new Promise<void>((resolve, reject) => {
        router.delete(invoiceItemsPath(id), {
          preserveScroll: true,
          preserveState: true,
          only: ["items", "invoice"],
          onSuccess: () => {
            setCellErrors((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((key) => {
                if (key.startsWith(`${id}:`)) {
                  delete next[key];
                }
              });
              return next;
            });
            toast.success("Ítem eliminado");
            resolve();
          },
          onError: (errors) => {
            const message =
              (Object.values(errors)[0] as string | undefined) ??
              "No se pudo eliminar el ítem";
            toast.error(message);
            reject(new Error(message));
          },
          onFinish: () => {
            setDeletingItemId(null);
          },
        });
      });
    },
    [canEditItems, invoiceItemsPath],
  );

  // Mark as reviewed action
  const handleMarkAsReviewed = useCallback(() => {
    router.post(
      markReviewedHref,
      {},
      {
        preserveScroll: true,
        onSuccess: () => setShowMarkReviewedDialog(false),
      },
    );
  }, [markReviewedHref]);

  // Create direct purchase action
  const handleCreateDirectPurchase = useCallback(
    (sectorId: number, startApproval: boolean) => {
      setIsCreatingDirectPurchase(true);
      router.post(
        createDirectPurchaseHref,
        { sector_id: sectorId, start_approval: startApproval },
        {
          preserveScroll: true,
          onSuccess: () => {
            setShowDirectPurchaseDialog(false);
          },
          onError: (errors) => {
            const errorMessage = Object.values(errors)[0] as string | undefined;
            if (errorMessage) {
              toast.error(errorMessage);
            }
          },
          onFinish: () => {
            setIsCreatingDirectPurchase(false);
          },
        },
      );
    },
    [createDirectPurchaseHref],
  );

  // Delete invoice action
  const handleDeleteInvoice = useCallback(() => {
    if (
      !window.confirm(
        "¿Estás seguro de que deseas eliminar esta factura? Esta acción no se puede deshacer y eliminará todos los ítems y archivos asociados.",
      )
    ) {
      return;
    }

    router.delete(invoiceHref, {
      onError: () => toast.error("Error al eliminar la factura"),
    });
  }, [invoiceHref]);

  // Determine conditions for direct purchase button
  const showDirectPurchaseButton =
    !invoice.has_purchase_request &&
    invoice.status !== "processing" &&
    invoice.status !== "error" &&
    invoice.status !== "uploaded";

  const isDirectPurchaseDisabled =
    !invoice.can_create_direct_purchase ||
    !invoice.supplier?.id ||
    invoice.status !== "reviewed";

  const getDirectPurchaseMessage = () => {
    if (invoice.has_purchase_request) {
      return "Esta factura ya está vinculada a una solicitud de compra";
    }
    if (!invoice.supplier?.id) {
      return "Asigna un proveedor para crear una compra directa";
    }
    if (invoice.status === "uploaded") {
      return "La factura está siendo procesada. Podrás crear una compra directa cuando esté revisada";
    }
    if (invoice.status === "pending_review") {
      return "Marca la factura como revisada para crear una compra directa";
    }
    return "Esta compra no permite cotizaciones adicionales. Solo admite aprobaciones, comentarios y remitos";
  };

  // Item actions: delete only for supervisor
  const itemActions: EditableDataTableAction<InvoiceItem>[] = canEditItems
    ? [
        {
          id: "delete",
          label: "Eliminar",
          icon: Trash2,
          variant: "destructive",
          confirm: {
            title: "Eliminar ítem",
            description: "Esta acción no se puede deshacer.",
            confirmLabel: "Eliminar",
            cancelLabel: "Cancelar",
          },
          onSelect: (row: InvoiceItem) => handleDeleteItem(row),
          isDisabled: (row: InvoiceItem) => deletingItemId === row.id,
        },
      ]
    : [];

  const status = statusConfig[invoice.status];

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Factura ${invoice.invoice_number ?? invoice.id}`} />

      <div className="mx-auto w-full max-w-5xl">
        <div className="flex flex-col gap-8 p-8">
          {/* Invoice Header */}
          <InvoiceHeader invoice={invoice} status={status} />

          {/* Processing Alert */}
          {showProcessingAlert && (
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              Procesando tu factura… Esta página se actualizará automáticamente.
            </div>
          )}

          <Separator />

          {/* Info Grid: Supplier, Date, Currency */}
          <div className="grid gap-6 md:grid-cols-3">
            <div className="space-y-2">
              <InlineEditableAsyncCombobox
                label="Proveedor"
                value={invoice.supplier?.id}
                field="supplier_id"
                updateUrl={invoiceHref}
                icon={User}
                formatValue={() =>
                  invoice.supplier?.name ?? "Proveedor por asignar"
                }
                searchEndpoint={supplierSearchEndpoint}
                disabled={!invoice.can_update}
                refreshKeys={["invoice", "items"]}
              />
              {invoice.tax_id_extracted && (
                <p className="text-xs text-muted-foreground">
                  CUIT: {invoice.tax_id_extracted}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                Fecha de Factura
              </div>
              <p className="text-base font-medium">
                {invoice.invoice_date
                  ? formatDateAR(invoice.invoice_date)
                  : "-"}
              </p>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">
                Moneda
              </div>
              <p className="text-base font-medium">{invoice.currency}</p>
            </div>
          </div>

          <Separator />

          {/* Purchase Request Section */}
          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-base font-medium">Solicitud de Compra</h2>
              <p className="text-sm text-muted-foreground">
                {invoice.purchase_request
                  ? "Esta factura está vinculada a una solicitud de compra"
                  : "Vincula esta factura con una solicitud de compra"}
              </p>
            </div>

            <PurchaseRequestPickerWithPreview
              value={invoice.purchase_request?.id ?? null}
              disabled={!invoice.can_update}
              supplierId={invoice.supplier?.id ?? undefined}
              mode="show"
              resource={{ type: "invoice", id: invoice.id }}
              placeholder="Buscar por número o sector..."
            />

            {/* Direct Purchase Button */}
            {showDirectPurchaseButton && (
              <Item variant="outline">
                <ItemMedia variant="icon">
                  <ShoppingCart className="size-4" />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>¿Compra sin solicitud previa?</ItemTitle>
                  <ItemDescription>
                    {getDirectPurchaseMessage()}
                  </ItemDescription>
                </ItemContent>
                <ItemActions>
                  <Button
                    onClick={() => setShowDirectPurchaseDialog(true)}
                    size="sm"
                    variant="default"
                    disabled={isDirectPurchaseDisabled}
                  >
                    Crear compra directa
                  </Button>
                </ItemActions>
              </Item>
            )}
          </div>

          <Separator />

          {/* Financial Summary (Componentized) */}
          <FinancialSummary invoice={invoice} />

          <Separator />

          {/* Invoice Items Table */}
          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-lg font-medium">Items de la Factura</h2>
              <p className="text-sm text-muted-foreground">
                Productos y servicios facturados
              </p>
            </div>
            <EditableDataTable
              columns={itemColumns}
              rows={items}
              getRowId={(item) => item.id}
              onCommit={handleCellCommit}
              onCommitSuccess={({ columnId }) => {
                const headerNode = itemColumns.find(
                  (column) => column.id === columnId,
                )?.header;
                const label =
                  typeof headerNode === "string" ? headerNode : "Campo";
                toast.success(`${label} actualizado`);
              }}
              errors={cellErrors}
              onAddRow={undefined}
              rowActions={itemActions}
              actionsHeader="Acciones"
              isBusy={deletingItemId !== null}
              emptyMessage="No hay items en esta factura"
            />
          </div>

          <Separator />

          {/* Review Action */}
          <div className="flex justify-end">
            <Button
              variant="default"
              onClick={() => setShowMarkReviewedDialog(true)}
              disabled={!invoice.can_be_marked_as_reviewed}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Marcar como revisado
            </Button>
          </div>

          {/* Attachments Section (Componentized) */}
          {hasAttachments && (
            <>
              <Separator />
              <AttachmentsSection
                processedPdf={invoice.processed_pdf}
                invoiceFiles={invoice.invoice_files}
              />
            </>
          )}

          {/* Observations */}
          {invoice.observations && (
            <>
              <Separator />
              <div className="space-y-3">
                <h2 className="text-base font-medium">Observaciones</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed break-words max-w-prose">
                  {invoice.observations}
                </p>
              </div>
            </>
          )}

          <Separator />

          <CommentsThread
            comments={comments ?? []}
            commentsPath={commentsHref}
            resourceLabel="la factura"
          />

          <Separator />

          {/* Back to List */}
          <div className="flex justify-between items-center">
            {invoice.can_delete ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteInvoice}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar factura
              </Button>
            ) : (
              <div /> /* Spacer */
            )}
            <Button variant="ghost" size="sm" asChild>
              <Link href={invoicesHref}>Volver a listado</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Mark as Reviewed Dialog */}
      <MarkAsReviewedDialog
        open={showMarkReviewedDialog}
        onOpenChange={setShowMarkReviewedDialog}
        resourceType="invoice"
        resourceData={{ has_purchase_request: invoice.has_purchase_request }}
        onConfirm={handleMarkAsReviewed}
      />

      {/* Create Direct Purchase Dialog */}
      <CreateDirectPurchaseDialog
        open={showDirectPurchaseDialog}
        onOpenChange={setShowDirectPurchaseDialog}
        onConfirm={handleCreateDirectPurchase}
        isSubmitting={isCreatingDirectPurchase}
      />
    </AppLayout>
  );
}
